import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Asset Imports
import clinicDashboard from '../assets/clinic_dashboard.png';
import queueDashboard from '../assets/queue_dashboard.png';
import androidPpe1 from '../assets/android_ppe_1.jpeg';
import androidPpe2 from '../assets/android_ppe_2.jpeg';
import androidPpe3 from '../assets/android_ppe_3.jpeg';
import harumWebsite from '../assets/harum_website.jpeg';
import logo from '../assets/logo.png';

// Image Groups for Slideshow
// We use objects for more control over positioning per image
const slideData = {
    web: [
        { src: clinicDashboard, focus: "center" },
        { src: queueDashboard, focus: "center" }
    ],
    mobile: [
        { src: androidPpe1, focus: "center" },
        { src: androidPpe2, focus: "center" },
        { src: androidPpe3, focus: "center" }
    ],
    digital: [
        { src: harumWebsite, focus: "center 20%" }, // Shifted down (20% from top)
        { src: logo, focus: "center" }
    ]
};

const Interface = () => {
    const navigate = useNavigate();

    return (
        <div className="flex flex-col md:flex-row h-screen w-full bg-white overflow-hidden font-montserrat">

            {/* --- Left Panel --- */}
            {/* Fixed Home Button */}
            <motion.button
                onClick={() => navigate('/')}
                className="fixed top-0 left-0 z-50 flex flex-col items-center group hidden md:flex"
                style={{ padding: '20px' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <motion.span
                    className="text-black text-sm font-medium"
                    style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)'
                    }}
                    whileHover={{ letterSpacing: "0.2em" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                >
                    HOME
                </motion.span>
                <motion.div
                    className="w-[1px] bg-black"
                    initial={{ height: '80px', opacity: 0.6 }}
                    whileHover={{ height: '100px', opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{ marginTop: '20px' }}
                />
            </motion.button>

            {/* Fixed Copyright */}
            <div className="fixed left-8 bottom-8 z-50 hidden md:block">
                <span
                    className="text-black/40 text-[10px] font-sans tracking-widest block"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                    © 2025
                </span>
            </div>

            <div className="w-full md:w-[35%] lg:w-[30%] h-auto md:h-full p-8 md:p-16 flex flex-col justify-center z-10 bg-white relative">
                {/* Mobile Top Bar - HOME + Line */}
                <div className="md:hidden flex items-center mb-6 w-full gap-4 pt-4">
                    <button
                        onClick={() => navigate('/')}
                        className="text-black text-sm font-medium tracking-widest uppercase"
                    >
                        HOME
                    </button>
                    <div className="h-[1px] bg-black w-24 opacity-20"></div>
                </div>

                {/* Main Text Content */}
                <div className="flex flex-col gap-2 pt-4 md:pt-32">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="text-[15vw] md:text-[8vw] lg:text-[7vw] leading-[0.8] font-bold tracking-tight text-black mb-4"
                    >
                        WORK
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        className="flex flex-col gap-1 pl-1"
                    >
                        <h2 className="text-lg md:text-xl font-medium text-black underline underline-offset-8">Selected Projects</h2>
                        <h2 className="text-lg md:text-xl font-light text-gray-400">Web Applications</h2>
                        <h2 className="text-lg md:text-xl font-light text-gray-400">Mobile Solutions</h2>
                        <h2 className="text-lg md:text-xl font-light text-gray-400">Digital Presence</h2>
                    </motion.div>
                </div>
            </div>

            {/* --- Right Panel (Grid System) --- */}
            <div className="w-full md:w-[65%] lg:w-[70%] h-[70vh] md:h-full bg-white relative overflow-hidden">
                <motion.div
                    className="grid grid-cols-2 md:grid-cols-3 grid-rows-3 h-full w-full gap-0" // gap-0 ensures no borders/gaps
                    initial="hidden"
                    animate="visible"
                    variants={{
                        hidden: { opacity: 0 },
                        visible: {
                            opacity: 1,
                            transition: {
                                staggerChildren: 0.15,
                                delayChildren: 0.4
                            }
                        }
                    }}
                >

                    {/* Item 1: Large Square-ish / Main Feature */}
                    <motion.div
                        className="col-span-1 md:col-span-2 row-span-2 relative overflow-hidden group"
                        variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                    >
                        <Slideshow
                            images={slideData.web}
                            label="Web Applications"
                            description="Designing scalable, high-performance web applications with modern frameworks and intuitive user flows."
                            interval={4000}
                        />
                    </motion.div>

                    {/* Item 2: Vertical Tall */}
                    <motion.div
                        className="col-span-1 row-span-2 relative overflow-hidden group bg-white"
                        variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                    >
                        <Slideshow
                            images={slideData.mobile}
                            label="Mobile Solutions"
                            description="Specializing in cross-platform mobile experiences that bring utility and elegance to the palm of your hand."
                            interval={4500}
                        />
                    </motion.div>

                    {/* Item 3: Wide Horizontal (Merged) */}
                    <motion.div
                        className="col-span-2 md:col-span-3 row-span-1 relative overflow-hidden group"
                        variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                    >
                        <Slideshow
                            images={slideData.digital}
                            label="Digital Presence"
                            description="Crafting a unique online identity through stunning landing pages and cohesive brand experiences."
                            interval={5000}
                        />
                    </motion.div>

                </motion.div>
            </div>

        </div>
    );
};

// Slideshow Component
const Slideshow = ({ images, label, description, interval = 3000 }) => {
    const [index, setIndex] = useState(0);
    const isSingleImage = images.length === 1;

    useEffect(() => {
        if (isSingleImage) return;

        const timer = setInterval(() => {
            setIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, interval);
        return () => clearInterval(timer);
    }, [images, interval, isSingleImage]);

    return (
        <div className="w-full h-full relative cursor-pointer overflow-hidden bg-white">
            <AnimatePresence initial={false} mode="popLayout">
                <motion.div
                    key={index}
                    className="absolute inset-0 w-full h-full bg-cover"
                    style={{
                        backgroundImage: `url(${images[index].src})`,
                        backgroundPosition: images[index].focus || 'center'
                    }}
                    initial={isSingleImage ? { scale: 1.1 } : { x: "100%" }}
                    animate={isSingleImage ? {
                        scale: [1.1, 1.2],
                        transition: { duration: 10, repeat: Infinity, repeatType: "reverse", ease: "linear" }
                    } : { x: 0 }}
                    exit={isSingleImage ? {} : { x: "-20%" }}
                    transition={isSingleImage ? {} : {
                        x: { type: "spring", stiffness: 300, damping: 30, duration: 0.8 },
                        opacity: { duration: 0.2 }
                    }}
                />
            </AnimatePresence>

            {/* Readability Gradient (Top Left) */}
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-br from-black/40 to-transparent z-10 opacity-80" />

            {/* Overlay on Hover */}
            <motion.div
                className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500 z-10"
            />

            {/* Label */}
            <div className="absolute top-6 left-6 z-20 overflow-hidden">
                <h3 className="text-[10px] font-bold tracking-[0.2em] text-white uppercase drop-shadow-md">
                    {label}
                </h3>
            </div>

            {/* Hover Content */}
            <div className="absolute inset-8 flex flex-col justify-end z-20 pointer-events-none">
                <motion.p
                    className="text-[10px] text-white/90 leading-relaxed font-light opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100"
                    style={{ maxWidth: '280px' }}
                >
                    {description}
                </motion.p>
            </div>

            {/* Plus Icon */}
            <div className="absolute top-6 right-6 z-20">
                <span className="text-xl font-light text-white drop-shadow-md">+</span>
            </div>
        </div>
    );
};

export default Interface;
